import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { ShieldCheck, Lock, Server, KeyRound, AlertTriangle, Users, Award, Database, Mail } from "lucide-react"
import { SEO } from "@/components/SEO"

const CustomerSecurityStatement = () => {
    return (
        <div className="min-h-screen bg-white font-sans text-slate-900">
            <SEO
                title="Customer Security Statement | RSA Tech Group"
                description="Learn about RSA Tech Group's comprehensive security framework, compliance certifications, and commitment to protecting customer data."
            />
            <Navigation />

            {/* Hero Section */}
            <div className="relative pt-48 pb-20 overflow-hidden bg-gradient-to-b from-[#0F172A] to-[#09090b]">
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-900/10 rounded-full blur-[120px] opacity-40 mix-blend-screen" />
                    <div className="absolute bottom-0 left-[-100px] w-[500px] h-[500px] bg-indigo-900/10 rounded-full blur-[100px] opacity-30" />
                </div>
                <div className="section-container relative z-10 text-center max-w-4xl mx-auto">
                    <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight text-white">
                        Customer Security Statement
                    </h1>
                    <p className="text-lg md:text-xl text-gray-300 leading-relaxed max-w-2xl mx-auto">
                        Our commitment to safeguarding your data with enterprise-grade security practices and industry-leading compliance standards.
                    </p>
                    <div className="mt-8 text-sm text-gray-400 font-medium tracking-wide uppercase">
                        Last Updated: January 2026
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <section className="py-24 bg-slate-50 relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('/digital-transform-bg-rectangles.png')] bg-cover bg-center opacity-[0.03]" />

                <div className="section-container relative z-10 max-w-4xl mx-auto">
                    <div className="bg-white/80 backdrop-blur-md border border-slate-200 rounded-2xl p-8 md:p-12 shadow-sm">

                        {/* 1. Our Commitment */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
                                    <ShieldCheck className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">1. Our Commitment</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>
                                    At RSA Tech Group, security is foundational to everything we build. We understand that our enterprise clients entrust us with critical business data and operations, and we take that responsibility seriously.
                                </p>
                                <p>
                                    Our security program is designed to protect the confidentiality, integrity, and availability of customer data across all our subsidiaries — including Connexr and LeoRix AI.
                                </p>
                            </div>
                        </div>

                        {/* 2. Security Framework */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
                                    <Award className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">2. Security Framework</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>Our security practices are aligned with and certified under the following industry standards:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li><strong>SOC 2 Type II:</strong> We undergo annual SOC 2 audits to validate our controls for security, availability, processing integrity, confidentiality, and privacy.</li>
                                    <li><strong>HIPAA:</strong> We maintain full HIPAA compliance for our healthcare clients, including Business Associate Agreements (BAAs) and appropriate administrative, physical, and technical safeguards.</li>
                                    <li><strong>ISO 27001:</strong> Our information security management system (ISMS) is aligned with ISO 27001 standards, providing a systematic approach to managing sensitive information.</li>
                                    <li><strong>GDPR:</strong> We comply with the General Data Protection Regulation for all personal data processing activities involving EU residents.</li>
                                </ul>
                            </div>
                        </div>

                        {/* 3. Data Protection */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
                                    <Database className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">3. Data Protection</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>We implement comprehensive data protection measures including:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li><strong>Encryption at Rest:</strong> All customer data is encrypted at rest using AES-256 encryption.</li>
                                    <li><strong>Encryption in Transit:</strong> All data in transit is protected using TLS 1.2 or higher.</li>
                                    <li><strong>Data Classification:</strong> We classify data based on sensitivity and apply appropriate handling procedures for each classification level.</li>
                                    <li><strong>Data Retention:</strong> We retain customer data only for as long as necessary to provide services and comply with legal obligations, with automated purging mechanisms.</li>
                                    <li><strong>Backup & Recovery:</strong> Automated, encrypted backups with regular disaster recovery testing ensure business continuity.</li>
                                </ul>
                            </div>
                        </div>

                        {/* 4. Infrastructure Security */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center text-purple-600">
                                    <Server className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">4. Infrastructure Security</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>Our infrastructure is designed with defense-in-depth principles:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Cloud infrastructure hosted on SOC 2 and ISO 27001 certified providers (AWS, Azure, GCP).</li>
                                    <li>Network segmentation and firewalls to isolate customer environments.</li>
                                    <li>Continuous vulnerability scanning and penetration testing performed by third-party security firms.</li>
                                    <li>24/7 monitoring via our Security Operations Center (SOC) with automated threat detection and response.</li>
                                    <li>Regular patching and update cycles with zero-day response procedures.</li>
                                </ul>
                            </div>
                        </div>

                        {/* 5. Access Controls */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center text-orange-600">
                                    <KeyRound className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">5. Access Controls</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>We enforce strict access control policies to ensure only authorized personnel can access customer data:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Role-based access control (RBAC) with the principle of least privilege.</li>
                                    <li>Multi-factor authentication (MFA) required for all system access.</li>
                                    <li>Regular access reviews and automatic deprovisioning upon role changes or termination.</li>
                                    <li>Comprehensive audit logging of all access events with tamper-evident storage.</li>
                                    <li>Background checks for all employees with access to customer data.</li>
                                </ul>
                            </div>
                        </div>

                        {/* 6. Incident Response */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center text-red-600">
                                    <AlertTriangle className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">6. Incident Response</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>
                                    RSA Tech Group maintains a formal Incident Response Plan that is tested and updated regularly. In the event of a security incident:
                                </p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Affected customers are notified within 72 hours of confirmed incidents, in compliance with applicable regulations.</li>
                                    <li>A dedicated incident response team coordinates containment, eradication, and recovery.</li>
                                    <li>Post-incident reviews are conducted to identify root causes and implement preventive measures.</li>
                                    <li>Detailed incident reports are made available to affected customers upon request.</li>
                                </ul>
                            </div>
                        </div>

                        {/* 7. Vendor Management */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center text-teal-600">
                                    <Users className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">7. Vendor Management</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>
                                    We hold our vendors and subprocessors to the same high security standards we maintain internally. Our vendor management program includes:
                                </p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>Security assessments and due diligence before onboarding any third-party vendor.</li>
                                    <li>Contractual security and privacy requirements, including data processing agreements.</li>
                                    <li>Ongoing monitoring and periodic reassessment of vendor security postures.</li>
                                    <li>A maintained list of subprocessors available to customers upon request.</li>
                                </ul>
                            </div>
                        </div>

                        {/* 8. Compliance & Certifications */}
                        <div className="mb-12">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center text-violet-600">
                                    <Lock className="w-5 h-5" />
                                </div>
                                <h2 className="text-2xl font-bold text-slate-900">8. Compliance & Certifications</h2>
                            </div>
                            <div className="space-y-4 text-slate-600 leading-relaxed pl-3 md:pl-14">
                                <p>Our compliance program is managed through Drata and covers:</p>
                                <ul className="list-disc pl-5 space-y-2">
                                    <li>SOC 2 Type II certification with annual audits.</li>
                                    <li>HIPAA compliance with annual risk assessments.</li>
                                    <li>ISO 27001 alignment with plans for formal certification.</li>
                                    <li>GDPR compliance including Data Protection Impact Assessments (DPIAs).</li>
                                    <li>CCPA compliance for California residents.</li>
                                </ul>
                                <p>
                                    Copies of our SOC 2 report and other compliance documentation are available to customers and prospects under NDA upon request.
                                </p>
                            </div>
                        </div>

                        {/* 9. Contact Us */}
                        <div className="mt-16 pt-12 border-t border-slate-100">
                            <h2 className="text-2xl font-bold text-slate-900 mb-6">9. Contact Us</h2>
                            <p className="text-slate-600 mb-6">
                                If you have questions about our security practices or would like to request compliance documentation, please contact us:
                            </p>
                            <div className="flex flex-col gap-2 pl-3 md:pl-0">
                                <div className="font-bold text-slate-900">RSA Tech Group — Security Team</div>
                                <div className="flex items-center gap-3 text-slate-800 font-medium">
                                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                                        <Mail className="w-4 h-4" />
                                    </div>
                                    <a href="mailto:security@rsatechgroup.com" className="hover:text-blue-600 transition-colors">security@rsatechgroup.com</a>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            <Footer />
        </div>
    )
}

export default CustomerSecurityStatement
